import { ColumnConfig, CustomColDef } from "./types";
import { getCodeData } from "@shared/utils/CommonUtils";
import MaskEditCellEditor from "./MaskEditCellEditor";
import dayjs from "dayjs";
import DateBoxCellEditor from "./DateBoxCellEditor";
import {
  ITooltipParams,
  ColDef,
  CellStyleFunc,
} from "ag-grid-charts-enterprise";

interface RowData {
  [key: string]: any; // 동적 키를 지원
}

// 유형별 컬럼 설정
const getColumnTypeConfig = async (
  column: ColumnConfig,
  user: any
): Promise<Partial<CustomColDef>> => {
  switch (column.tp_column) {
    case "SnTextBox":
      return {
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        // 필수값 표시 스타일
        // cellStyle: (params) => {
        //   if (column.yn_required === "Y" && !params.node?.rowPinned) {
        //     return { backgroundColor: "#ffcccc" }; // 입력이 없으면 테두리 강조
        //   }
        // },
      };

    case "SnNumberBox":
      return {
        cellEditor: "agNumberCellEditor",
        filter: "agNumberColumnFilter",

        valueFormatter: (params) => {
          // if (!params.value) return "0";
          if (!params.value || isNaN(params.value)) return "";

          // 숫자 포맷 (천 단위 구분)
          return Number(params.value).toLocaleString();
        },
        cellStyle: (params) => {
          return { textAlign: "right" }; // 오른쪽 정렬
        },
        cellEditorParams: {
          preventStepping: true, // 숫자 증감 동작을 비활성화 할 수 있음.
          suppressKeyboardEvent: (params: any) => {
            const { event } = params;
            // ArrowUp, ArrowDown 키 이벤트 무시
            return event.key === "ArrowUp" || event.key === "ArrowDown";
          },
        },
      };

    case "SnMaskEdit": {
      return {
        cellEditor: MaskEditCellEditor, // 커스텀 마스크 에디터 사용
        cellEditorParams: {
          maskType: column.maskType, // 마스크 유형 전달
        },
        valueSetter: (params) => {
          console.log("valueSetter called:", params.newValue); // 디버깅 로그

          if (params.colDef && params.colDef.field) {
            // field가 존재하는지 확인
            params.data[params.colDef.field] = params.newValue; // 값 설정
            console.log("Updated data:", params.data);

            return true;
          }
          return false; // 설정 실패 시 false 반환
        },
        valueGetter: (params) => {
          if (params.colDef && params.colDef.field) {
            console.log(
              "valueGetter called:",
              params.data[params.colDef.field]
            );
            return params.data[params.colDef.field] || ""; // 현재 데이터 반환
          }
        },
      };
    }

    case "SnDateBox": {
      return {
        cellEditor: DateBoxCellEditor,

        valueFormatter: (params) => {
          const value = params.value;
          return value ? dayjs(value, "YYYYMMDD").format("YYYY/MM/DD") : "";
        },
        cellStyle: {
          textAlign: "center", // 가운데 정렬
        },
      };
    }

    case "SnCheckBox": {
      return {
        // cellRenderer: (params: { value: string }) =>
        //   params.value === "Y" ? "✔" : "✖", // 렌더링 시 체크/언체크 표시
        // cellRenderer: "agCheckboxCellRenderer", // 기본 체크박스 렌더러 사용
        cellRendererSelector: (params: any) => {
          if (params.node?.rowPinned) {
            return { component: "" }; // footer는 아무것도 안 보여줌
          }
          return { component: "agCheckboxCellRenderer" };
        },
        cellEditor: "agCheckboxCellEditor", // 체크박스 편집기 사용
        cellEditorParams: {
          trueValue: "Y", // 체크된 값
          falseValue: "N", // 체크 해제된 값
        },
        valueSetter: (params: any) => {
          // 체크 상태에 따라 데이터에 Y/N 값 설정
          if (params.colDef && params.colDef.field) {
            params.data[params.colDef.field] = params.newValue ? "Y" : "N";
            return true;
          }
          return false;
        },
        valueGetter: (params: any) => {
          if (params.node?.rowPinned) return undefined;
          // 데이터에서 Y/N 값을 기반으로 체크 상태 반환
          return params.data[params.colDef.field] === "Y";
        },
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      };
    }

    case "SnComboBox": {
      const dataSource =
        (await getCodeData(column.cd_clas || "", column.dc_sql || "")) || []; // 기본값을 빈 배열로 설정

      return {
        cellEditor: "agRichSelectCellEditor", // Rich Select 에디터 사용
        cellEditorParams: {
          values: dataSource.map((item: { code: any }) => item.code), // 드롭다운에서 사용할 code 값
          formatValue: (value: string) => {
            // 드롭다운 내부에서 name 값 표시
            const selected = dataSource.find(
              (item: { code: string }) => item.code === value
            );
            return selected ? selected.name : value;
          },
        },
        cellRenderer: (params: { value: any }) => {
          // 셀에 name 값 표시
          const selected = dataSource.find(
            (item: { code: any }) => item.code === params.value
          );
          return selected ? selected.name : params.value;
        },
        valueGetter: (params: any) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          // 드롭다운에서 선택된 값을 데이터에 저장
          if (params.newValue) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.newValue;
            }

            return true; // 값이 변경되었음을 알림
          }
          return false;
        },
        // 필수값 표시 스타일
        // cellStyle: (params) => {
        //   if (column.yn_required === "Y" && !params.node?.rowPinned) {
        //     return { backgroundColor: "#fff3cd" };
        //   }
        // },
      };
    }

    case "SnCodeBox":
      return {
        // cellRenderer: (params: {
        //   focused: any;
        //   data: { [x: string]: any };
        // }) => {
        //   if (column.cd_displaycolumn) {
        //     return params.focused
        //       ? params.data?.[column.cd_column]
        //       : params.data?.[column.cd_displaycolumn] || "";
        //   } else {
        //     return params.data?.[column.cd_column] || "";
        //   }
        // },
        cellRenderer: (params: {
          focused: any;
          data: { [x: string]: any };
        }) => {
          const rawValue = params.data?.[column.cd_column];
          const isPasswordColumn = column.cd_popup === "POPUP_PASSWORD";

          if (isPasswordColumn) {
            return !rawValue ? "" : "••••••••••";
          }

          if (column.cd_displaycolumn) {
            const displayValue = params.data?.[column.cd_displaycolumn] || "";
            return params.focused ? rawValue : displayValue;
          } else {
            return rawValue || "";
          }
        },
        valueGetter: (params) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          if (params.newValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.newValue;
              return true;
            }
          } else if (params.oldValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.oldValue;
              return true;
            }
          }
          return false;
        },
        // 필수값 표시 스타일
        // cellStyle: (params) => {
        //   if (column.yn_required === "Y" && !params.node?.rowPinned) {
        //     return { backgroundColor: "#d9edf7" };
        //   }
        // },
      };

    case "SnMultiCodeBox":
      return {
        cellRenderer: (params: { focused: any; data: { [x: string]: any } }) =>
          params.focused
            ? params.data[column.cd_column]
            : params.data[column.cd_column],
        valueGetter: (params) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          if (params.newValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.newValue;
              return true;
            }
          } else if (params.oldValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.oldValue;
              return true;
            }
          }
          return false;
        },
      };

    default:
      return { cellEditor: "agTextCellEditor", filter: "agTextColumnFilter" };
  }
};

let result: any = null;

// result 값을 외부로 export
export const setResult = (newResult: any) => {
  result = newResult;
};

export const getResult = () => result;

//  `CellStyleFunc`을 정확히 따르도록 타입 지정
export const getCellStyle = (
  errorMap: Map<number, { fields: Set<string>; messages: Map<string, string> }>
) => {
  return ((params) => {
    //  Footer 행인지 먼저 체크 → rowPinned가 true면 스타일 적용 안 함
    if (params.node?.rowPinned) {
      return null; //  Footer(합계 행)는 스타일 적용 제외!
    }

    if (!params.node || !params.colDef) return null;

    const rowIndex = params.node?.rowIndex ?? -1;
    const colDef = params.colDef as ColDef<any, any>;
    ///  기존 스타일 가져오기
    let baseStyle: any = {};

    //  `cellStyle`이 함수인 경우 실행
    if (typeof colDef.cellStyle === "function") {
      try {
        baseStyle = colDef.cellStyle(params) || {};
      } catch (error) {
        console.error("Error executing cellStyle function:", error);
        baseStyle = {};
      }
    }
    //  `cellStyle`이 객체라면 그대로 사용
    else if (
      typeof colDef.cellStyle === "object" &&
      colDef.cellStyle !== null
    ) {
      baseStyle = { ...colDef.cellStyle };
    }

    console.log(" 기본 셀 스타일:", baseStyle);

    if (!colDef.field) return null; // 필드명이 없으면 무시

    // const errorInfo = errorMap.get(rowIndex);
    // if (errorInfo && errorInfo.fields.has(colDef.field)) {
    //   return { ...baseStyle, backgroundColor: "#ffcccc", color: "black" };
    // }

    return null;
  }) as CellStyleFunc<any, any>;
};

//  오류 메시지 툴팁 표시 함수
export const getTooltipValueGetter = (
  errorMap: Map<number, { fields: Set<string>; messages: Map<string, string> }>
) => {
  return ((params: ITooltipParams<any, any, any>) => {
    if (!params.node || !params.colDef) return null;

    const rowIndex = params.node.rowIndex ?? -1;
    // 'field' 속성이 존재하는 경우에만 처리
    if (!("field" in params.colDef) || !params.colDef.field) return null;

    const field = params.colDef.field ?? ""; // 기본값 제공

    if (!field) return null; // 필드명이 없으면 툴팁 표시 안 함

    const errorInfo = errorMap.get(rowIndex);

    if (errorInfo && errorInfo.messages.has(field)) {
      return errorInfo.messages.get(field) ?? ""; // 메시지가 없을 경우 빈 문자열 반환
    }

    return null;
  }) as (params: ITooltipParams<any, any, any>) => string | null;
};

// 컬럼 정의 생성 함수
export const createColumnDefs = async (
  columns: ColumnConfig[],
  isEditable: (field: string) => boolean, // editable 여부를 제어하는 콜백 함수
  user: any,
  errorMap: Map<number, { fields: Set<string>; messages: Map<string, string> }> //  오류 컬럼 & 메시지 저장
): Promise<CustomColDef[]> => {
  const columnDefs = await Promise.all(
    columns
      .sort((a, b) => a.no_seq - b.no_seq)
      .map(async (column) => {
        const typeConfig = await getColumnTypeConfig(column, user);

        return {
          // 필수값 표시 스타일
          // headerName:
          //   column.yn_required === "Y"
          //     ? ` ${column.nm_column} ` // 필수 입력값 컬럼이면 헤더에 아이콘 추가
          //     : column.nm_column,

          headerClass:
            column.yn_required === "Y" ? "required-header" : "normal-header", //  필수 입력값 컬럼이면 스타일 적용

          headerName: column.nm_column, // 컬럼 헤더
          aggFunc: column.yn_sum === "Y" ? "sum" : undefined, // 그룹했을 때 합계되도록
          field: column.cd_column, // 필드 이름
          initialWidth: column.qt_width, // 필드 크기
          width: column.qt_width, // 필드 크기
          hide: column.fg_visible !== "Y", // 표시 여부
          suppressColumnsToolPanel: column.fg_visible === "H", // 컬럼리스트에 안나오게
          sortable: column.yn_allowsorting === "Y", // 정렬 여부
          pinned:
            column.yn_fixed === "Y" &&
            (column.tp_fixedposition === "left" ||
              column.tp_fixedposition === "right")
              ? (column.tp_fixedposition as "left" | "right")
              : null,

          editable: isEditable(column.cd_column), // 부모에서 제어하는 editable 여부
          // tp_column 설정
          tp_column: column.tp_column,
          yn_required: column.yn_required,
          cd_clas: column.cd_clas,
          cd_popup: column.cd_popup,
          tp_format: column.tp_format,
          dc_editcon: column.dc_editcon,
          cd_displaycolumn: column.cd_displaycolumn,
          yn_sum: column.yn_sum,
          cd_user: column.cd_user,
          yn_drilldown: column.yn_drilldown,
          ...typeConfig,
          // cellStyle: getCellStyle(errorMap), //  모든 오류 컬럼에 cellStyle 적용 (빨간색)
          tooltipValueGetter: getTooltipValueGetter(errorMap) as any, // 마우스 오버 시  메시지 툴팁 적용
          suppressNavigable: (params: any) =>
            params.node.rowPinned === "bottom", // 방향키로 풋터에 포커스 들어가지 못하도록
        };
      })
  );

  return columnDefs;
};
